import { Subject } from "rxjs";
import { BaseGroupType, GroupViewBy } from "src/AngularJS/Models/group-type.model";
import type { GroupSelectorDTO } from "src/AngularJS/Reusables/group-selector.models";
import { ThresholdErrorTypes } from "src/app/dashboard/modals/widget-thresholds-modal/widget-thresholds-modal.model";
import { FilterableGroupingDTO } from "src/app/reusables/group-view-by-selector/group-view-by-selector.component";
import { AttributeSelectorModel, AttributeType, SelectedCategoryModel } from "src/shared/models/attribute-selector.models";
import type { DashboardModuleResponsiveInstance } from "src/shared/models/module-info.model";
import { DOW, ModuleManagerDateRangeInfo, ModuleManagerMetricInfo, ModuleManagerModuleSettings, TimeInterval } from "src/shared/models/module-manager/module-manager-settings.models";
import { WallboardColumnSortSettings } from "../../app/wallboards/wallboards.model";
import { AttributeSelectorServiceBase } from "../services/attribute-selector-service-base";
import { ClearViewUtils } from "../utils";
import { IModuleCustomSettingSection } from "./module-custom-settings-section.model";
import { SharedEventEmitter } from "./shared-event-emitter";

export type GroupTypeInfo = { level: number; name: string };

export type SelectedLevelChangedArgs = {
    groupType: BaseGroupType;
    groupLevel: number;
    groupLevelName: string;
    filterableGroupingDTO?: FilterableGroupingDTO;
};
export default class SlideoutSettingsManager {
    empSelectorDTO: GroupSelectorDTO;
    recSelectorDTO: GroupSelectorDTO;

    userAttributesModel: AttributeSelectorModel;
    dataAttributesModel: AttributeSelectorModel;

    selectedToggle: BaseGroupType = BaseGroupType.Employee;
    selectedLevel: number;
    filterableGroupingDTO: FilterableGroupingDTO = null;
    selectedLevelName: string;
    selectedRecCount: number;
    selectedEmpCount: number;
    /** used by the report widget for dynamic levels on group filters */
    settingEmpLevels: number[];
    /** used by the report widget for dynamic levels on group filters */
    settingRecLevels: number[];

    empFilterLevelName: string;
    recFilterLevelName: string;

    showSelectMetrics: boolean;
    selectedMetrics: ModuleManagerMetricInfo[] = [];
    selectedMetricIDs: number[] = [];

    selectedDateRange: ModuleManagerDateRangeInfo;
    isTimeSelectorEnabled: boolean;
    invertTimeSelector: boolean;
    selectedDaysOfWeek: DOW;
    intervalAmt: number;
    intervalType: TimeInterval;
    fifteenMinutesIntervalVisible: boolean;
    selectedMetricsText = '';
    availableDateRanges: ModuleManagerDateRangeInfo[];
    attributesText = 'No Attributes Chosen';

    waitBeforeApplying: Promise<any>[] = [];
    groupSelectorShowToggle = true;
    groupSelectorToggleReady = false;
    showInitialLevelName = true

    generalWallboardSettings: any;
    wallboardColumnSortSettings: WallboardColumnSortSettings;

    // EVENTS
    onAttributeReloaded = new Subject<AttributeSelectorModel>();
    onSelectedDateRangeChanged = new Subject<ModuleManagerDateRangeInfo>(); // reload groups
    onSelectedEmpGroupsChanged = new Subject<GroupSelectorDTO>(); // reload rec
    onSelectedRecGroupsChanged = new Subject<GroupSelectorDTO>(); // reload emp // reload metric
    onSelectedGroupViewByChanged = new Subject<GroupViewBy>(); // reload metric
    onSelectedReportChanged = new Subject<void>(); // select report
    onSettingsCancelled = new Subject<void>();
    onSettingsApplied = new Subject<void>();
    beforeSettingsApplied = new Subject<void>();
    onPlaceCustomSettings = new Subject<IModuleCustomSettingSection>();
    empGroupTypesChanged = new SharedEventEmitter<void>();
    recGroupTypesChanged = new SharedEventEmitter<void>();
    groupToggleChanged = new Subject<GroupSelectorDTO>();
    managerViewByUpdated = new SharedEventEmitter<void>();
    selectedMetricsChanged = new SharedEventEmitter<void>();
    onThresholdErrorChanged? = new Subject<ThresholdErrorTypes>();

    private _minTime = 0;
    private _minTimeDate = new Date(new Date().setHours(0, 0, 0, 0));
    get minTime() { return this._minTime; }
    set minTime(v: number) {
        if (this._minTime !== v) {
            this._minTime = v;
            const hour = Math.floor(v);
            const minutes = Math.floor((v - hour) * 60);
            this._minTimeDate = new Date(new Date().setHours(hour, minutes, 0, 0));
        }
    }
    get minTimeDate() { return this._minTimeDate; }
    set minTimeDate(v: Date) {
        this._minTimeDate = v;
        if (this._minTimeDate === undefined)
            return;
        const hour = v.getHours();
        const minutes = v.getMinutes();
        this._minTime = hour + (minutes / 60);
    }

    private _maxTime = 24;
    private _maxTimeDate = new Date(new Date().setHours(24, 0, 0, 0));
    get maxTime() { return this._maxTime; }
    set maxTime(v: number) {
        if (this._maxTime !== v) {
            this._maxTime = v;
            const hour = Math.floor(v);
            const minutes = Math.floor((v - hour) * 60);
            this._maxTimeDate = new Date(new Date().setHours(hour, minutes, 0, 0));
        }
    }
    get maxTimeDate() { return this._maxTimeDate; }
    set maxTimeDate(v: Date) {
        this._maxTimeDate = v;
        if (this._minTimeDate === undefined)
            return;
        const hour = v.getHours();
        const minutes = v.getMinutes();
        this._maxTime = hour + (minutes / 60);
    }

    private _disableOrInvertTimeSelector = null;
    get disableOrInvertTimeSelector() { return this._disableOrInvertTimeSelector; }
    set disableOrInvertTimeSelector(v: boolean) {
        this._disableOrInvertTimeSelector = v;
        if (v === null) {
            this.isTimeSelectorEnabled = false;
            this.invertTimeSelector = false;
        } else {
            this.isTimeSelectorEnabled = true;
            this.invertTimeSelector = v;
        }
    }

    private _empGroupTypes: GroupTypeInfo[] = [];
    get empGroupTypes() { return this._empGroupTypes; }
    set empGroupTypes(v: GroupTypeInfo[]) {
        this._empGroupTypes = v;
        this.selectedEmpCount = this.getSelectedCount(BaseGroupType.Employee);
        this.empGroupTypesChanged.emit();
    }

    private _recGroupTypes: GroupTypeInfo[] = [];
    get recGroupTypes() { return this._recGroupTypes; }
    set recGroupTypes(v: GroupTypeInfo[]) {
        this._recGroupTypes = v;
        this.selectedRecCount = this.getSelectedCount(BaseGroupType.Recording);
        this.recGroupTypesChanged.emit();
    }

    constructor(
        private attributeSelectorService: AttributeSelectorServiceBase,
        module?: DashboardModuleResponsiveInstance,
    ) {
        if (module) {
            this.groupSelectorShowToggle = module.GroupSelectorShowToggle;
        }
    }

    setDefaultSettings() {
        this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject({
            name: 'Today',
            displayName: 'Today',
            utcOffset: 0,
        });
        this.selectedDaysOfWeek = 127 as DOW;
        this._disableOrInvertTimeSelector = null;
        this.isTimeSelectorEnabled = false;
        this.invertTimeSelector = false;
        this.minTime = 0;
        this.maxTime = 0;

        this.intervalAmt = 1;
        this.intervalType = 2;

        this.empSelectorDTO = {
            ApplicationID: 0,
            PageName: "",
            SelectedGroupLevel: 1,
            SelectedGroupType: BaseGroupType.Employee,
            SelectedMinLevel: 1,
            SelectedMaxLevel: 3,
            _SelectedGroup1IDList: [0],
            _SelectedGroup2IDList: [0],
            _SelectedGroup3IDList: [0],
            _SelectedGroup4IDList: [0],
            _SelectedGroup5IDList: [0],
            ModuleRender: false,
            ShiftRender: false,
            AllSelected: false,
            toggleGroupData: false
        };

        this.recSelectorDTO = {
            ApplicationID: 0,
            PageName: "",
            SelectedGroupLevel: 1,
            SelectedGroupType: BaseGroupType.Recording,
            SelectedMinLevel: 1,
            SelectedMaxLevel: 4,
            _SelectedGroup1IDList: [0],
            _SelectedGroup2IDList: [0],
            _SelectedGroup3IDList: [0],
            _SelectedGroup4IDList: [0],
            _SelectedGroup5IDList: [0],
            ModuleRender: false,
            ShiftRender: false,
            AllSelected: false,
            toggleGroupData: false
        };

        this.selectedLevelName = "";
        this.selectedToggle = BaseGroupType.Employee;

        this.dataAttributesModel = {
            GroupedCategoryID: 0,
            SelectedCategories: [],
            Type: AttributeType.Data
        };

        this.userAttributesModel = {
            GroupedCategoryID: 0,
            SelectedCategories: [],
            Type: AttributeType.User
        };
    }

    setSettings(controlSettings: ModuleManagerModuleSettings) {
        this.selectedToggle = controlSettings.selectedGroupType;
        this.selectedMetrics = controlSettings.selectedMetrics;
        this.selectedMetricIDs = controlSettings.selectedMetrics?.map(m => m.Id);
        this.selectedLevel = controlSettings.selectedGroupLevel;
        this.selectedLevelName = controlSettings.selectedLevelName;
        this.empSelectorDTO = controlSettings.empSelectorDTO;
        this.recSelectorDTO = controlSettings.recSelectorDTO;
        if (controlSettings.selectedFilterableGroupingDTO)
            this.filterableGroupingDTO = controlSettings.selectedFilterableGroupingDTO;

        this.dataAttributesModel = controlSettings.dataAttributesModel;
        this.userAttributesModel = controlSettings.userAttributesModel;

        this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(controlSettings.selectedDateRange);
        this.isTimeSelectorEnabled = controlSettings.isTimeSelectorEnabled;
        this.invertTimeSelector = controlSettings.invertTimeSelector;
        this._disableOrInvertTimeSelector = this.isTimeSelectorEnabled ? this.invertTimeSelector : null;
        this.selectedDaysOfWeek = controlSettings.selectedDaysOfWeek;
        this.minTime = controlSettings.minTime;
        this.maxTime = controlSettings.maxTime;
        this.intervalAmt = controlSettings.intervalAmt;
        this.fifteenMinutesIntervalVisible = controlSettings.fifteenMinutesIntervalVisible;
        this.intervalType = controlSettings.intervalType;
    }

    getSelectedCount(groupType: BaseGroupType) {
        const dto = this.getSelectorByType(groupType);
        if (!dto)
            return 0;

        if (this.groupSelectorShowToggle && groupType === this.selectedToggle) { // is set to "View by"
            const groupsForLevel: number[] = dto[`_SelectedGroup${dto.SelectedGroupLevel}IDList`];
            if (groupsForLevel.length === 0 || (groupsForLevel.length === 1 && groupsForLevel[0] === 0))
                return 0; // ALL
            else
                return groupsForLevel.length;
        } else { // just a filter
            // find lowest level with selections
            let filterLevelName = '';
            if (dto.SelectedMaxLevel <= 0) // user doesn't have access to groups
                return 0;
            const groupTypes = (groupType === BaseGroupType.Employee) ? this.empGroupTypes : this.recGroupTypes;

            for (let i = dto.SelectedMinLevel; i <= dto.SelectedMaxLevel; i++) {
                if (i < 1)
                    continue;

                const groupsForLevel: number[] = dto[`_SelectedGroup${i}IDList`];
                const currGroupLevel = groupTypes.find(gt => gt.level === i);
                filterLevelName = currGroupLevel?.name || '';
                if (groupsForLevel.length === 0 || (groupsForLevel.length === 1 && groupsForLevel[0] === 0)) {
                    continue; // ALL
                } else {
                    switch (groupType) {
                        case BaseGroupType.Employee:
                            this.empFilterLevelName = filterLevelName;
                            break;
                        case BaseGroupType.Recording:
                            this.recFilterLevelName = filterLevelName;
                            break;
                    }
                    return groupsForLevel.length; // found a selection
                }
            }

            // all levels had ALL
            filterLevelName = groupTypes[groupTypes.length - 1]?.name; // lowest level

            switch (groupType) {
                case BaseGroupType.Employee:
                    this.empFilterLevelName = filterLevelName;
                    break;
                case BaseGroupType.Recording:
                    this.recFilterLevelName = filterLevelName;
                    break;
            }

            return 0;
        }
    }

    getSelectorByType(type: BaseGroupType) {
        if (type === BaseGroupType.Employee)
            return this.empSelectorDTO;
        else if (type === BaseGroupType.Recording)
            return this.recSelectorDTO;

        return null;
    }

    onSelectedLevelChanged(e: SelectedLevelChangedArgs) {
        this.selectedToggle = e.groupType;
        this.selectedLevelName = e.groupLevelName;
        this.selectedLevel = e.groupLevel;
        this.filterableGroupingDTO = e.filterableGroupingDTO;
        const selectorDTO = this.getSelectorByType(e.groupType);
        if (selectorDTO != null) // emp or rec only
            selectorDTO.SelectedGroupLevel = e.groupLevel;
        this.selectedEmpCount = this.getSelectedCount(BaseGroupType.Employee);
        this.selectedRecCount = this.getSelectedCount(BaseGroupType.Recording);
        this.groupSelectorToggleReady = true;
        this.managerViewByUpdated.emit();
    }

    onGroupToggleChanged(dto: GroupSelectorDTO) {
        if (dto.SelectedGroupType == BaseGroupType.Employee){
            this.empSelectorDTO = dto;
        } else {
            this.recSelectorDTO = dto;
        }
    }

    onEmpGroupsChanged(dto: GroupSelectorDTO) {
        this.empSelectorDTO = dto;
        this.selectedEmpCount = this.getSelectedCount(BaseGroupType.Employee);
    }

    onRecGroupsChanged(dto: GroupSelectorDTO) {
        this.recSelectorDTO = dto;
        this.selectedRecCount = this.getSelectedCount(BaseGroupType.Recording);
    }


    async onSelectedAttributesChanged(model: AttributeSelectorModel) {
        this.attributesText = await this.getAttributeContextDisplay();

        // If no attribute attribute is selected but said attribute is toggle on,
        // set selected toggle to default (Lvl.1 Employee).
        if (
            (this.selectedToggle === BaseGroupType.DataAttribute &&
                !this.dataAttributesModel?.SelectedCategories.length) ||
            (this.selectedToggle === BaseGroupType.UserAttribute &&
                !this.userAttributesModel?.SelectedCategories.length)
        ) {
            this.onSelectedLevelChanged({
                groupType: BaseGroupType.Employee,
                groupLevel: 1,
                groupLevelName: this.empGroupTypes.find((group) => group.level === 1)?.name
            });
        }
    }

    async getAttributeContextDisplay() {
        const selectedDataCount = this.dataAttributesModel?.SelectedCategories?.length ?? 0;
        const selectedUserCount = this.userAttributesModel?.SelectedCategories?.length ?? 0;
        const totalSelectedCategories = selectedDataCount + selectedUserCount;

        if (totalSelectedCategories === 0)
            return this.attributeSelectorService.localizationText.noAttributes;
        else if (totalSelectedCategories === 1) {
            let categoryModel: SelectedCategoryModel;
            let selectedType: AttributeType;
            if (selectedDataCount === 1) {
                categoryModel = this.dataAttributesModel.SelectedCategories[0];
                selectedType = AttributeType.Data;
            } else if (selectedUserCount === 1) {
                categoryModel = this.userAttributesModel.SelectedCategories[0];
                selectedType = AttributeType.User;
            }

            const categories = (await this.attributeSelectorService.loadCategories(selectedType)).AttributeCategoryModels;
            const category = categories.find(cat => cat.CategoryID === categoryModel.SelectedCategoryID);

            if (categoryModel.SelectedAttributeIDs.length === 0 || categoryModel.SelectedAttributeIDs.indexOf(-1) >= 0)
                return `${category?.CategoryName} (${this.attributeSelectorService.localizationText.all})`;
            else if (categoryModel.SelectedAttributeIDs.length === 1) {
                const selectedAttributeID = categoryModel.SelectedAttributeIDs[0];
                if (selectedAttributeID === 0)
                    return `${category?.CategoryName} (${this.attributeSelectorService.localizationText.notSet})`;
                else {
                    const attributeValues = await this.attributeSelectorService.loadValues(category.CategoryID, selectedType);
                    const selectedAttribute = attributeValues.find(att => att.ID === selectedAttributeID);
                    return `${category.CategoryName} (${selectedAttribute?.Name ?? '1'})`;
                }
            } else {
                return `${category.CategoryName} (${categoryModel.SelectedAttributeIDs.length})`;
            }
        } else {
            let totalAttributes = 0;

            // add up selected data attributes
            for (const cat of this.dataAttributesModel?.SelectedCategories ?? []) {
                if (cat.SelectedAttributeIDs.length === 0 || cat.SelectedAttributeIDs.indexOf(-1) >= 0) {
                    const allAttributes = await this.attributeSelectorService.loadValues(cat.SelectedCategoryID, AttributeType.Data);
                    totalAttributes += (allAttributes?.length ?? 0) + 1; // +1 for Not Set
                } else {
                    totalAttributes += (cat.SelectedAttributeIDs?.length ?? 0);
                }
            }

            for (const cat of this.userAttributesModel?.SelectedCategories ?? []) {
                if (cat.SelectedAttributeIDs.length === 0 || cat.SelectedAttributeIDs.indexOf(-1) >= 0) {
                    const allAttributes = await this.attributeSelectorService.loadValues(cat.SelectedCategoryID, AttributeType.User);
                    totalAttributes += (allAttributes?.length ?? 0) + 1; // +1 for Not Set
                } else {
                    totalAttributes += (cat.SelectedAttributeIDs?.length ?? 0);
                }
            }

            const localization = this.attributeSelectorService.localizationText;
            return `${totalSelectedCategories} ${localization.categories} & ${totalAttributes} ${localization.attributes}`;
        }
    }

    updateSelectedMetricsText(clip = true) {
        if (this.selectedMetrics.length > 0) {
            this.showSelectMetrics = false;
            if (clip) {
                this.selectedMetricsText = ClearViewUtils.joinWithMax(this.selectedMetrics.map(m => m.CustomName && m.CustomName != "" ? m.CustomName+"*" : m.Abbreviation), 40, ', ');
            } else {
                this.selectedMetricsText = this.selectedMetrics.map(m =>  m.CustomName && m.CustomName != "" ? m.CustomName+"*" : m.Abbreviation).join(', ');
            }
        } else {
            this.showSelectMetrics = true;
            this.selectedMetricsText = '';
        }
        this.selectedMetricsChanged.emit();
    }

    loadFromState(state: string) {
        try {
            const simplified = JSON.parse(state);
            this.empSelectorDTO = simplified.empSelectorDTO;
            this.recSelectorDTO = simplified.recSelectorDTO;
            this.userAttributesModel = simplified.userAttributesModel;
            this.dataAttributesModel = simplified.dataAttributesModel;
            this.selectedToggle = simplified.selectedToggle;
            this.selectedLevelName = simplified.selectedLevelName;
            this.selectedRecCount = simplified.selectedRecCount;
            this.selectedEmpCount = simplified.selectedEmpCount;
            this.empFilterLevelName = simplified.empFilterLevelName;
            this.recFilterLevelName = simplified.recFilterLevelName;
            this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(simplified.selectedDateRange);
            this.isTimeSelectorEnabled = !!simplified.isTimeSelectorEnabled;
            this.invertTimeSelector = simplified.invertTimeSelector;
            this._disableOrInvertTimeSelector = this.isTimeSelectorEnabled ? this.invertTimeSelector : null;
            this.selectedDaysOfWeek = simplified.selectedDaysOfWeek;
            this.minTime = simplified.minTime;
            this.maxTime = simplified.maxTime;
            this.intervalAmt = simplified.intervalAmt;
            this.intervalType = simplified.intervalType;
            return true;
        } catch {
            return false;
        }
    }

    toStateString() {
        const simplified = {
            empSelectorDTO: this.empSelectorDTO,
            recSelectorDTO: this.recSelectorDTO,
            userAttributesModel: this.userAttributesModel,
            dataAttributesModel: this.dataAttributesModel,
            selectedToggle: this.selectedToggle,
            selectedLevelName: this.selectedLevelName,
            selectedRecCount: this.selectedRecCount,
            selectedEmpCount: this.selectedEmpCount,
            empFilterLevelName: this.empFilterLevelName,
            recFilterLevelName: this.recFilterLevelName,
            selectedDateRange: this.selectedDateRange,
            isTimeSelectorEnabled: this.isTimeSelectorEnabled,
            invertTimeSelector: this.invertTimeSelector,
            selectedDaysOfWeek: this.selectedDaysOfWeek,
            minTime: this.minTime,
            maxTime: this.maxTime,
            intervalAmt: this.intervalAmt,
            intervalType: this.intervalType
        };

        return JSON.stringify(simplified);
    }

    placeCustomSettings(component: IModuleCustomSettingSection) {
        this.onPlaceCustomSettings.next(component);
    }

    thresholdErrorChanged(thresholdErrors: ThresholdErrorTypes) {
        this.onThresholdErrorChanged.next(thresholdErrors);
    }
}
